import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { stripLocaleFromPath } from "@/i18n/locale-utils";
import { ogLocaleFor } from "@/lib/seo/hreflang";
import { useHead, type HeadTag } from "@/lib/seo/useHead";

interface SeoProps {
  title: string;
  description?: string;
  canonical?: string;
  image?: string;
  type?: "website" | "article" | "product";
  /** OG image template variant: catalog, blog, case-study, bootcamp, default */
  ogVariant?: "default" | "catalog" | "blog" | "case-study" | "bootcamp";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Emit robots noindex for this route only. */
  noindex?: boolean;
  /** 0 = route-level default, 1 = page-level (default, wins over route-level). */
  priority?: number;
}

const OG_FN_BASE = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/og-image`;

export function Seo({ title, description, canonical, image, type = "website", ogVariant, jsonLd, noindex, priority = 1 }: SeoProps) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);

  // Re-render on an in-place language switch (no route change) so the head is
  // rewritten immediately with the new locale's strings.
  const [, setTick] = useState(0);
  useEffect(() => {
    const cb = () => setTick((t) => t + 1);
    i18n.on("languageChanged", cb);
    return () => i18n.off("languageChanged", cb);
  }, [i18n]);

  const fullTitle = title.length > 60 ? title.slice(0, 57) + "…" : title;
  const desc = description ? (description.length > 160 ? description.slice(0, 157) + "…" : description) : undefined;
  // Canonical: prefer explicit prop. Fallback strips query + hash so UTMs / preview flags
  // never leak into rel=canonical.
  const url =
    canonical ??
    (typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}`
      : undefined);

  // Auto-generate OG image from edge function when ogVariant given and no explicit image.
  const resolvedImage = image ?? (ogVariant
    ? `${OG_FN_BASE}?type=${encodeURIComponent(ogVariant)}&title=${encodeURIComponent(fullTitle)}${desc ? `&subtitle=${encodeURIComponent(desc)}` : ""}`
    : undefined);

  const jsonLdArr = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  const tags: HeadTag[] = [];
  const meta = (attrs: Record<string, string>) => tags.push({ tag: "meta", attrs });
  const link = (attrs: Record<string, string>) => tags.push({ tag: "link", attrs });

  if (desc) meta({ name: "description", content: desc });
  if (noindex) meta({ name: "robots", content: "noindex, follow" });
  if (url) link({ rel: "canonical", href: url });

  // hreflang alternates — URL-based locale prefix.
  if (url) {
    try {
      const u = new URL(url);
      const { rest } = stripLocaleFromPath(u.pathname);
      const path = rest === "/" ? "" : rest;
      const buildHref = (loc: string) => `${u.origin}/${loc}${path}${u.search}${u.hash}`;
      for (const l of SUPPORTED_LANGUAGES) link({ rel: "alternate", hreflang: l, href: buildHref(l) });
      link({ rel: "alternate", hreflang: "x-default", href: buildHref("en") });
    } catch {
      /* non-absolute canonical — skip alternates */
    }
  }

  meta({ property: "og:title", content: fullTitle });
  if (desc) meta({ property: "og:description", content: desc });
  meta({ property: "og:type", content: type });
  meta({ property: "og:locale", content: ogLocaleFor(lang) });
  for (const l of SUPPORTED_LANGUAGES) {
    if (l !== lang) meta({ property: "og:locale:alternate", content: ogLocaleFor(l) });
  }
  meta({ property: "og:site_name", content: "Bootpreneurs" });
  if (url) meta({ property: "og:url", content: url });
  if (resolvedImage) meta({ property: "og:image", content: resolvedImage });

  meta({ name: "twitter:card", content: "summary_large_image" });
  meta({ name: "twitter:title", content: fullTitle });
  if (desc) meta({ name: "twitter:description", content: desc });
  if (resolvedImage) meta({ name: "twitter:image", content: resolvedImage });

  for (const node of jsonLdArr) {
    tags.push({ tag: "script", attrs: { type: "application/ld+json" }, content: JSON.stringify(node) });
  }

  useHead({ title: fullTitle, lang, tags }, priority);

  return null;
}
